import { useEffect, useRef } from 'react'
import { Track, type Room } from 'livekit-client'

const BARS = 16

type Props = {
  room: Room | null
  label: string
  source: 'local' | 'agent'
  active: boolean
}

type Tap = { ctx: AudioContext; analyser: AnalyserNode; data: Uint8Array<ArrayBuffer>; src: MediaStreamAudioSourceNode }

function mediaTrack(room: Room, source: 'local' | 'agent'): MediaStreamTrack | null {
  if (source === 'local') {
    const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
    return pub?.track?.mediaStreamTrack ?? null
  }
  for (const p of room.remoteParticipants.values()) {
    for (const pub of p.audioTrackPublications.values()) {
      if (pub.track?.mediaStreamTrack) return pub.track.mediaStreamTrack
    }
  }
  return null
}

/** Level bars driven by the live audio (Web Audio analyser), not server speaker events. */
export default function VoiceMeter({ room, label, source, active }: Props) {
  const barsRef = useRef<(HTMLSpanElement | null)[]>([])

  useEffect(() => {
    let raf = 0
    let tap: Tap | null = null
    let tapped: MediaStreamTrack | null = null
    const smooth = new Array(BARS).fill(0)

    const release = () => {
      tap?.src.disconnect()
      void tap?.ctx.close()
      tap = null
      tapped = null
    }

    const frame = () => {
      const track = room && active ? mediaTrack(room, source) : null
      if (track !== tapped) {
        release()
        if (track && track.readyState === 'live') {
          const ctx = new AudioContext()
          const analyser = ctx.createAnalyser()
          analyser.fftSize = 128
          analyser.smoothingTimeConstant = 0.6
          const src = ctx.createMediaStreamSource(new MediaStream([track]))
          src.connect(analyser)
          tap = { ctx, analyser, data: new Uint8Array(analyser.frequencyBinCount), src }
          tapped = track
        }
      }
      if (tap) tap.analyser.getByteFrequencyData(tap.data)
      for (let i = 0; i < BARS; i++) {
        const raw = tap ? tap.data[Math.floor((i / BARS) * 40) + 1] / 255 : 0
        smooth[i] = Math.max(raw, smooth[i] * 0.85)
        const el = barsRef.current[i]
        if (el) el.style.transform = `scaleY(${Math.max(0.08, Math.min(1, smooth[i] * 1.4))})`
      }
      raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => {
      cancelAnimationFrame(raf)
      release()
    }
  }, [room, source, active])

  return (
    <div className={`voice-meter ${source}${active ? '' : ' off'}`} data-testid={`voice-meter-${source}`}>
      <span className="voice-meter-label">{label}</span>
      <div className="voice-meter-bars" aria-hidden="true">
        {Array.from({ length: BARS }, (_, i) => (
          <span key={i} ref={(el) => { barsRef.current[i] = el }} />
        ))}
      </div>
    </div>
  )
}
